// Modul „Fragebögen" (Questionnaire-Autofill) — deutsche QUELLE.
//
// Deckt `questionnaires/Uebersicht.tsx`, `Review.tsx`, `FrageKarte.tsx`,
// `Anlagen.tsx` und die Enum-Texte aus `questionnaires/labels.ts` ab.
//
// Warum ein eigener Namespace und nicht `ui/labels.ts`: die Status-, Konfidenz-
// und Anker-Maps sind DOMÄNEN-Texte („Bereit zur Prüfung" heisst hier etwas
// anderes als ein globales `review`), und `ui/labels.ts` ist die geteilte
// Schicht. Dieselbe Begründung wie bei `datasets.status`.
//
// Was hier bewusst NICHT steht (und auch in der englischen Fassung nicht
// übersetzt wird):
//
// * **„Yes" / „No" / „N/A"** aus `FrageKarte.tsx` — das sind die WERTE, die
//   wörtlich in das (englische) Kundendokument geschrieben werden, keine
//   Beschriftungen. Übersetzt wären sie ein falscher Dokumenteninhalt. Was der
//   Katalog dafür trägt, ist der erklärende Tooltip (`jaNeinHinweis`).
// * **Datums- und Zahlformate** (`labels.ts::datum`, `de-DE`) — `docs/i18n.md`
//   §2: die bleiben in jeder Sprache deutsch. Der Platzhalter
//   `datumPlatzhalter` steht deshalb in beiden Katalogen als „TT.MM.JJJJ".
// * Produktnamen und technische Werte (`product_slug`, Modell-IDs, Seed-IDs).
export const questionnaires = {
  // ── Zustände + Enums ──────────────────────────────────────────────────────
  // Nur der TEXT. Badge-Farbe, „läuft gerade"-Flag und Icon bleiben in
  // `questionnaires/labels.ts` — sie gehören zum Zustand, nicht zur Sprache.
  // Bewusst OHNE `as Record<string, string>`: nur mit einzelnen Keys im Typ
  // meldet tsc einen in ../en/questionnaires.ts vergessenen Zustand.
  status: {
    uploaded: "Wartet auf Verarbeitung",
    profiling: "Format wird geprüft",
    extracting: "Fragen werden gelesen",
    matching: "Antworten werden gesucht",
    review: "Bereit zur Prüfung",
    approved: "Geprüft",
    filled: "Ausgefüllt",
    rejected: "Kein Fragebogen",
    error: "Fehler",
  },
  /** Stufe + Zähler am laufenden Bogen („Antworten werden gesucht — 23/70").
   *  Die Stufe kommt aus `status` oben, die Zahlen vom Server. */
  stufeMitZaehler: (stufe: string, erledigt: number, gesamt: number) =>
    `${stufe} — ${erledigt}/${gesamt}`,
  konfidenz: {
    high: "sicher",
    medium: "mittel",
    low: "unsicher",
  },
  // Der Antworttyp („Ja/Nein", „Freitext") stand bis 08/2026 als Chip auf jeder
  // Karte. Er ist Technik-Vokabular des Extraktors und sagte dem QM nichts —
  // die Zeile zeigt stattdessen das passende Bedienelement. Der Anker-Typ
  // unten wird weiterhin gebraucht: für die Zellen, auf die KEINE Frage zeigt.
  ankerArt: {
    acroform: "PDF-Formularfeld",
    docx_sdt: "Word-Inhaltssteuerelement",
    docx_checkbox: "Word-Kontrollkästchen",
    docx_table: "Word-Tabellenzelle",
    docx_paragraph: "Word-Absatz",
    xlsx_cell: "Excel-Zelle",
    pdf_overlay: "PDF-Stempel (Geometrie)",
    "xlsx-bereits-ausgefuellt": "bereits ausgefüllt",
  },

  /** Alter eines „Stand"-Datums (labels.ts::alter). Das DATUM selbst bleibt
   *  deutsch formatiert, das Alter ist Fliesstext und wird übersetzt. */
  alterDiesenMonat: "diesen Monat",
  alterMonate: (n: number) => `${n} Monate alt`,
  alterJahre: (n: number) => `${n} Jahre alt`,

  // ── Übersicht (Liste + Upload) ────────────────────────────────────────────
  titel: "Fragebögen",
  beschreibung:
    "Kunden-Fragebögen hochladen, Antworten vorschlagen lassen, jede Antwort selbst freigeben.",
  listeFehlgeschlagen: "Fragebögen konnten nicht geladen werden",
  hochgeladen: "Hochgeladen",
  wirdGelesen: (datei: string) => `${datei} wird jetzt gelesen.`,
  uploadFehlgeschlagen: "Upload fehlgeschlagen",
  loeschenTitel: (datei: string) => `„${datei}" löschen?`,
  loeschenBeschreibung:
    "Der Lauf, alle extrahierten Fragen und die hochgeladene Kundendatei werden entfernt.",
  loeschen: "Löschen",
  geloescht: "Gelöscht",
  loeschenFehlgeschlagen: "Löschen fehlgeschlagen",
  kunde: "Kunde",
  kundePlatzhalter: "z. B. Mary Kay",
  produkt: "Produkt",
  produktHinweis: "Bestimmt, welche Produktfakten in den Vorschlägen eingesetzt werden.",
  keinProduktbezug: "kein Produktbezug",
  ablegeZone: "Fragebogen hierher ziehen oder klicken",
  ablegeFormate: "PDF, DOCX oder XLSX — ältere .doc/.xls bitte vorher umspeichern.",
  dateiAria: "Fragebogen auswählen",
  wirdHochgeladen: "wird hochgeladen …",
  laeufe: "Läufe",
  wirdGeladen: "wird geladen …",
  leerTitel: "Noch kein Fragebogen hochgeladen",
  leerHinweis:
    "Lade den Fragebogen des Kunden hoch — das System liest die Fragen und schlägt Antworten aus der freigegebenen Bibliothek vor.",
  dateiWaehlen: "Datei auswählen",
  fragenAnzahl: (n: number) => `${n} Fragen`,
  pruefen: "Prüfen",
  fragebogenLoeschen: "Fragebogen löschen",
  fragebogenLoeschenAria: (datei: string) => `${datei} löschen`,

  // ── Review: Meldungen ─────────────────────────────────────────────────────
  speichernFehlgeschlagen: "Speichern fehlgeschlagen",
  neuSuchenFehlgeschlagen: "Neu suchen fehlgeschlagen",
  sektionFehlgeschlagen: "Sektion umstellen fehlgeschlagen",
  uebernehmenFehlgeschlagen: "Übernehmen fehlgeschlagen",
  entscheidungFehlgeschlagen: "Entscheidung fehlgeschlagen",
  vorschlaegeUebernommen: (n: number) => `${n} Vorschläge übernommen`,
  nurKonfidenzSicher: "Nur Konfidenz „sicher“.",
  entscheidungGespeichert: "Entscheidung gespeichert",
  folgefragenGesetzt: (n: number, gelten: boolean) =>
    `${n} Folgefrage(n) ${gelten ? "freigegeben" : "auf „nicht zutreffend“ gesetzt"}.`,
  ohneFolgefragen: "Ohne Folgefragen in diesem Bogen.",
  dokumentWirdAusgefuellt: "Dokument wird ausgefüllt",
  /** P3/P4: das gefüllte Original wurde erzeugt und wird im Reiter angezeigt. */
  dokumentGefuellt: (n: number) =>
    `Original ausgefüllt — ${n} Antwort(en) eingetragen.`,
  dokumentGefuelltRest: (n: number) =>
    `${n} Stelle(n) konnten nicht automatisch gefüllt werden — bitte im Dokument von Hand ergänzen.`,
  ausfuellenFehlt: "Ausfüllen ist noch nicht ausgeliefert",
  ausfuellenFehltDetail:
    "Der Endpunkt /fill gehört zum Writer-Paket (P3) und fehlt auf diesem Server noch.",
  ausfuellenFehlgeschlagen: "Ausfüllen fehlgeschlagen",

  // ── Review: Kopf + Fortschritt ────────────────────────────────────────────
  nichtErreichbar: "Fragebogen nicht erreichbar",
  zurUebersicht: "Zur Übersicht",
  wirdGeladenLang: "Fragebogen wird geladen …",
  /** Detailseite eines Bogens, der noch verarbeitet wird. Sie zeigt dann die
   *  Stufe statt einer leeren Prüfliste — die sähe aus wie „nichts gefunden". */
  laufHinweis:
    "Die Prüfliste erscheint, sobald der Lauf durch ist. Die Seite aktualisiert sich selbst.",
  alleFragebogen: "Alle Fragebögen",
  vorschlaegeModell: (modell: string) => `Vorschläge: ${modell}`,
  geprueftVon: (reviewt: number, anwendbar: number) => `${reviewt} von ${anwendbar} geprüft`,
  offenUndLuecken: (offen: number, luecken: number) =>
    `${offen} offen · ${luecken} ohne belegte Antwort`,
  entscheidungenTeil: (n: number) => ` · ${n} Entscheidung(en)`,
  ausgeklammertTeil: (n: number) => ` · ${n} ausgeklammert`,
  lueckenTeil: (n: number) => ` · ${n} ohne belegte Antwort`,
  sichereUebernehmen: (n: number) => `${n} sichere Vorschläge übernehmen`,

  // ── D-3: Zellen, auf die keine Frage zeigt ────────────────────────────────
  // Der Server liefert sie als `fortschritt.offene_stellen` (Zahl) und
  // `offene_stellen` (Liste) mit. Sie sind der Grund, warum die Oberfläche
  // NICHT mehr „Alles bearbeitet" sagen darf, solange welche offen sind: der
  // Prüfer kann nur prüfen, was ihm gezeigt wird (T9 §4/D-3).
  offeneStellenTeil: (n: number) =>
    n === 1 ? " · 1 Zelle nicht bearbeitet" : ` · ${n} Zellen nicht bearbeitet`,
  offeneStellenTitel: (n: number) =>
    n === 1 ? "1 Zelle ohne Frage" : `${n} Zellen ohne Frage`,
  offeneStellenErklaerung:
    "Das sind Antwortzellen und Felder des Originaldokuments, auf die keine extrahierte Frage zeigt. Sie werden maschinell NICHT befüllt und müssen von Hand bearbeitet werden.",
  offeneStellenWeitere: (n: number) => `… und ${n} weitere`,
  /** Teilmenge der offenen Stellen mit art="xlsx-bereits-ausgefuellt"
   *  (Extraktions-Härtung 10): die Gegenseite hat das Feld schon beantwortet,
   *  bitop schuldet hier nichts — eigene, nicht-warnende Erklärung statt der
   *  „muss von Hand bearbeitet werden"-Formel oben. */
  bereitsAusgefuelltTitel: (n: number) =>
    n === 1 ? "1 Feld bereits ausgefüllt" : `${n} Felder bereits ausgefüllt`,
  bereitsAusgefuelltErklaerung:
    "Diese Felder sind im Originaldokument schon beantwortet — von der Gegenseite, nicht von bitop. Es fehlt hier nichts.",

  // ── Review: Reiter + Lücken ───────────────────────────────────────────────
  reiterFragen: "Fragen",
  reiterLuecken: "Lücken",
  reiterAnlagen: "Anlagen",
  reiterDokument: "Dokument",

  // ── Übersicht: Registerkarten + kuratierte Dokumentenliste (WH 2026-08-21) ──
  reiterFragebogen: "Fragebögen",
  reiterDokumente: "Dokumente",
  dokRegistryHinweis:
    "Die kuratierte Liste der aktiven Belege — nur aus diesen wird beim Ausfüllen gezogen. Die Dateien liegen in Salesforce; „Open SF“ öffnet den Datensatz dort.",
  dokOpenSf: "Open SF",
  dokSuche: "Suchen (Titel, ID) …",
  dokTypAlle: "Alle Typen",
  dokProduktAlle: "Alle Produkte",
  dokGlobal: "global",
  dokNurAktive: "nur aktive",
  dokAnzahl: (n: number, gesamt: number) => `${n} von ${gesamt} Dokumenten`,
  dokKeine: "Keine Dokumente",
  dokKeineHinweis: "Kein Eintrag passt zu den Filtern.",
  dokLadenFehler: "Dokumentenliste konnte nicht geladen werden",
  dokSpeichernFehler: "Änderung konnte nicht gespeichert werden",
  dokAbgelaufen: "abgelaufen",
  dokAktiv: "aktiv",
  dokInaktiv: "inaktiv",
  dokAktivUmschalten: "Wird gezogen / nicht gezogen — umschalten",
  dokAktuellUmschalten: "„aktuell“ / „veraltet“ — umschalten",
  dokSfPlatzhalter: "Salesforce-Link (https://…)",
  dokSfLabel: "Salesforce-Link",
  dokKeinSfLink: "kein SF-Link",
  dokGueltigBisLabel: "gültig bis",
  lueckenHinweis:
    "Für diese Fragen gibt es keine belegte Antwort in der Bibliothek. Beantworte sie hier von Hand — die Antwort gilt nur für diesen Fragebogen. Weitere Aktionen (u. a. erneut suchen) stehen hinter dem ⓘ jeder Zeile. (Belege, deren Herausgabe zu entscheiden ist, stehen als Entscheidung bei den Fragen — nicht hier.)",
  quelleEntwurf: "Entwurf",
  quelleEntwurfTooltip: (frage: string) =>
    `Noch nicht kuratiert (Entwurf) — ${frage}. Vorschläge auf dieser Grundlage werden nie „sicher" und nicht sammelweise übernommen.`,
  konfidenzGrund: (grund: string) => `Konfidenz gekappt: ${grund}`,

  keineLuecken: "Keine Lücken",
  keineLueckenHinweis: "Zu jeder anwendbaren Frage gab es einen belegten Vorschlag.",

  // ── Review: Abschluss-Leiste ──────────────────────────────────────────────
  allesBearbeitet: (reviewt: number, luecken: number) =>
    `Alles bearbeitet — ${reviewt} freigegeben, ${luecken} ohne belegte Antwort.`,
  /** Dieselbe Lage, aber mit unbearbeiteten Zellen: bewusst OHNE „Alles
   *  bearbeitet" (D-3). */
  bearbeitetMitOffenenStellen: (reviewt: number, luecken: number, stellen: number) =>
    `${reviewt} freigegeben, ${luecken} ohne belegte Antwort · ${stellen} ${
      stellen === 1 ? "Zelle" : "Zellen"
    } nicht bearbeitet.`,
  nochOffen: (n: number) => `Noch ${n} offen`,
  dokumentAusfuellen: "Dokument ausfüllen",
  /** Die Attestierung: EIN Knopf am Ende statt eines Hakens je Zeile. Der Satz
   *  muss „Dokument ausfüllen" wörtlich enthalten — mehrere Walks selektieren
   *  genau diese Beschriftung (docs/i18n.md §4.4). */
  geprueftAusfuellen: "Geprüft — Dokument ausfüllen",
  geprueftAusfuellenTooltip:
    "Bestätigt die noch offenen sicheren Antworten und füllt danach das Dokument. Mittlere, unsichere und unbelegte Fragen bleiben einzeln zu prüfen.",
  /** Zweite Toast-Zeile zur Sammelübernahme aus der Attestierung — sie sagt,
   *  was gerade STILL passiert ist. Ein Zustandssprung ohne Meldung sähe aus
   *  wie ein Fehler. */
  attestierungHinweis:
    "Mit der Prüfung übernommen. Alles Übrige bleibt offen und einzeln zu prüfen.",

  // ── Dokument-Reiter: gefülltes Dokument ansehen + Paket herunterladen ──────
  dokumentReiterHinweis: "Das ausgefüllte Dokument, so wie es abgegeben wird.",
  dokumentLaedt: "Vorschau wird erzeugt …",
  dokumentLeerTitel: "Noch nicht ausgefüllt",
  dokumentLeerHinweis: "Fülle das Dokument aus, um es hier anzusehen.",
  dokumentFehlerTitel: "Vorschau nicht verfügbar",
  dokumentNochNicht:
    "Es gibt noch keine gefüllte Fassung — bitte zuerst „Dokument ausfüllen“.",
  dokumentNeuFuellen: "Erneut ausfüllen",
  paketDownload: "Download (Paket)",
  /** Dateiname des Paket-Downloads (an den Kundendokument-Namen angehängt). */
  paketDatei: "-paket.zip",
  paketLeer: "Nichts zum Herunterladen",
  paketFehlgeschlagen: "Download fehlgeschlagen",

  // ── D-9: abgelehnter Bogen ────────────────────────────────────────────────
  abgelehntTitel: "Dieser Fragebogen wurde abgelehnt",
  abgelehntZeile: "Abgelehnt — dieses Dokument wird nicht ausgefüllt.",
  abgelehntKeinAusfuellen:
    "Ein abgelehntes Dokument wird nicht ausgefüllt — bitte den richtigen Fragebogen hochladen.",

  // ── Bekannte Fähigkeits-Lücken (`format_profile.unsupported`) ─────────────
  // Der Bogen läuft normal durch die Pipeline; die Meldungen selbst kommen
  // fertig formuliert vom Server (profiler.py) und werden NICHT übersetzt —
  // sie nennen Dateipfade und Konzept-Paragraphen. Übersetzt ist nur der
  // Rahmen, der sagt, WAS die Liste ist.
  einschraenkungenTitel: "Bekannte Einschränkungen dieses Dokuments",
  einschraenkungenHinweis:
    "Der Fragebogen wird trotzdem ausgefüllt — diese Stellen kann das System aber nicht lesen oder zurückschreiben. Bitte von Hand prüfen.",

  // ── Review: Sektionen ─────────────────────────────────────────────────────
  ohneSektion: "Ohne Sektion",
  sektionGeprueft: (geprueft: number, gesamt: number) => `${geprueft}/${gesamt} geprüft`,
  giltNicht: "gilt für bitop nicht",
  giltNichtSetzen: "Gilt für bitop nicht",
  wiederAufnehmen: "Wieder aufnehmen",
  sektionAusgeklammert: (grund: string) => `Von der Extraktion ausgeklammert: ${grund}`,
  sektionVonHand:
    "Von Hand ausgeklammert — die Fragen bleiben im Dokument leer (Konzept B4). „Wieder aufnehmen“ macht das rückgängig.",

  // ── Review: Entscheidungs-Block ───────────────────────────────────────────
  entscheidungNoetig: (position: number) => `Entscheidung nötig — Frage #${position}`,
  entscheidungsKontext: "Entscheidungs-Kontext",
  kanonFrageZeile: (frage: string) => `Kanon-Frage: „${frage}"`,
  freitextCommercial: "Freitext — das Commercial Team entscheidet den Wortlaut.",
  folgefragenBleiben: "Folgefragen bleiben zu beantworten",
  folgefragenBleibenJa: "Die abhängigen Fragen bleiben in der Liste.",
  folgefragenBleibenNein:
    "Die abhängigen Fragen werden als „nicht zutreffend“ ausgeklammert.",
  notizPlatzhalter: "Notiz zur Entscheidung (optional) — z. B. wer sie getroffen hat",
  entscheidungSpeichern: "Entscheidung speichern",

  // ── Review: Kontaktrollen ─────────────────────────────────────────────────
  // Ein Ansprechpartner wird ABGEFRAGT und nie geraten: der falsche Mensch in
  // einem Kundendokument ist der Schadensfall. Die Texte sagen das ausdrücklich
  // — sonst liest der Prüfer die Vorbelegung als Vorschlag des Systems.
  kontaktFrage: (rolle: string) => `Wer ist ${rolle} für diesen Bogen?`,
  kontaktRollen: {
    commercial: "der kaufmännische Ansprechpartner",
    quality: "der Ansprechpartner Qualität",
    technical: "der technische Ansprechpartner",
    ceo: "die Geschäftsführung",
    signer: "der Unterzeichner",
  },
  kontaktFeld: {
    name: "Name",
    titel: "Position / Funktion",
    email: "E-Mail",
    telefon: "Telefon",
  },
  kontaktHinweis:
    "Vorbelegt aus dem Faktenblatt — bitte prüfen und bei Bedarf überschreiben. Das System schlägt hier nichts aus der Antwort-Bibliothek vor.",
  kontaktZielFelder: (liste: string) =>
    `In dieses Formularfeld geschrieben wird: ${liste}`,
  kontaktFehlendeFakten: (liste: string) =>
    `Im Faktenblatt nicht hinterlegt: ${liste}`,
  kontaktUebernehmen: "Kontakt übernehmen",

  // ── Fragekarte: Eingabe ───────────────────────────────────────────────────
  /** D-11: Erklärung zu den unübersetzten Werten Yes/No/N/A. */
  jaNeinHinweis:
    "Yes / No / N/A werden wörtlich so in das Dokument geschrieben — sie sind der Antwortwert, keine Beschriftung.",
  eigenerText: "oder eigener Text",
  bitteWaehlen: "— bitte wählen —",
  /** Datumsformat bleibt in JEDER Sprache deutsch (docs/i18n.md §2). */
  datumPlatzhalter: "TT.MM.JJJJ",

  // ── Fragekarte: Belege + Zustand ──────────────────────────────────────────
  kanonFrageTooltip: (frage: string) => `Kanon-Frage: ${frage}`,
  faktAusFaktenblatt: "Fakt aus dem Faktenblatt",
  korrigiert: "korrigiert",
  uebernommen: "übernommen",
  ausgelassen: "ausgelassen",
  /** Der Anker steht NUR noch da, wo er fehlt — dann ist er eine Warnung und
   *  keine Beschriftung: die Antwort ist exportierbar, aber nicht einfügbar. */
  ankerFehltKurz: "nicht automatisch einfügbar",
  keinAnkerTooltip:
    "Ohne Anker kann die Antwort nur exportiert, nicht ins Dokument geschrieben werden.",
  korrekturSpeichern: "Korrektur speichern",
  abbrechen: "Abbrechen",
  /** yes_no_text: Platzhalter für das Angabe-Feld neben Ja/Nein. */
  spezifikationPlatzhalter: "Angabe (z. B. Häufigkeit, Temperatur) …",
  /** „Do you have X? If yes, attach Y": Beleg-Marker neben dem Ja/Nein. */
  anlageMarker: (code: string, titel: string): string => {
    switch (code) {
      case "anlage_beilegen":
        return titel ? `Beleg beilegen: ${titel}` : "Beleg beilegen";
      case "anlage_vertraulich":
        return titel ? `Beleg (Freigabe nötig): ${titel}` : "Beleg — Freigabe nötig";
      case "anlage_abgelaufen":
        return titel ? `Beleg abgelaufen: ${titel}` : "Beleg abgelaufen";
      case "anlage_fehlt":
        return "Beleg fehlt — Herausgabe entscheiden";
      default:
        return titel ? `Beleg prüfen: ${titel}` : "Beleg prüfen";
    }
  },
  herkunftKorrigiert: "korrigiert",
  herkunftEntschieden: "entschieden",
  herkunftVorschlag: "Vorschlag übernommen",
  keineBelegteAntwort: "Keine belegte Antwort.",
  /** Norm-Guard. Die Normbezeichnungen selbst („ISO 9001") sind international
   *  und werden nicht übersetzt — nur der Satz darum. */
  normKonflikt: (quelle: string, frage: string) =>
    `Die zitierte Quelle betrifft ${quelle}, gefragt ist ${frage} — eine Auskunft zu einer anderen Norm belegt diese Frage nicht.`,
  naechsterKandidat: (frage: string, seed: string, score: string, schwelle: string) =>
    `Am nächsten dran: „${frage}" (${seed}${score}) — unter der Schwelle ${schwelle}.`,
  entscheidetCommercial:
    "Diese Frage entscheidet das Commercial Team — das System füllt sie nicht vor.",
  entscheiden: "Entscheiden",
  nochNichtGesucht: "Für diese Frage wurde noch nicht gesucht.",
  fehlendeFakten: (liste: string) => `Fehlende Fakten im Faktenblatt: ${liste}`,
  standDerFakten: (liste: string) => `Stand der benutzten Fakten: ${liste}`,
  faktStand: (datum: string, wieAlt: string) =>
    wieAlt ? `Stand ${datum} (${wieAlt})` : `Stand ${datum}`,
  faktOffen: (todo: string) => `offen: ${todo}`,
  verworfeneQuellen: (liste: string) =>
    `Verworfene (nicht belegte) Quellenangaben: ${liste}`,

  // ── Fragenzeile: Ein-Klick, Langfassung, Details ──────────────────────────
  // Die Zeile ersetzt seit 08/2026 die Karte (70 Fragen = 20.084 px Scrollweg).
  // Was sie zeigt, muss ohne Karte verständlich bleiben — deshalb tragen die
  // Tooltips hier mehr als sonst.
  vorschlagTooltip: "Vorschlag des Systems — ein Klick darauf übernimmt ihn.",
  /** Eine sichere Antwort steht als Klartext da, als wäre sie fertig. Sie ist
   *  fachlich noch nicht freigegeben — dieser Tooltip sagt genau das, damit
   *  „sieht erledigt aus" nicht zu „ist erledigt" wird. */
  sichererVorschlagTooltip:
    "Sichere Antwort aus der Bibliothek — sie wird mit „Geprüft — Dokument ausfüllen“ übernommen. „Ändern“ öffnet sie.",
  antwortEintragen: "Antwort eintragen",
  antwortBearbeitenTooltip: "Klicken zum Bearbeiten",
  konfidenzPunktTooltip: (stufe: string) => `Konfidenz: ${stufe}`,
  details: "Details",
  detailsTooltip: "Belege, Vorbehalte und weitere Aktionen zu dieser Frage",
  /** `answer` geht ins Formularfeld, `answer_long` NUR in den Antwortbogen
   *  (`writer.py`). Ohne diese Beschriftung standen beide untereinander und
   *  lasen sich wie doppelter Text (Tester-Rückfrage zu Frage 13). */
  langfassung: "Langfassung",
  langfassungTitel: "Zusätzliche Information",
  kanonFrageQuelle: (quelle: string, frage: string) => `${quelle} — „${frage}"`,

  // ── Review: Abschnitts-Sprungleiste ───────────────────────────────────────
  sprungleisteTitel: "Abschnitte",
  sprungZiel: (name: string, geprueft: number, gesamt: number) =>
    `Zu „${name}" springen — ${geprueft} von ${gesamt} geprüft`,

  // ── Fragenzeile: Aktionen ─────────────────────────────────────────────────
  uebernehmen: "Übernehmen",
  aendern: "Ändern",
  korrigieren: "Korrigieren",
  auslassen: "Auslassen",
  zuruecksetzen: "Zurücksetzen",
  neuSuchen: "Neu suchen",
  neuSuchenTooltip:
    "Fragt die Antwort-Bibliothek erneut — z. B. nachdem ein Eintrag ergänzt wurde.",

  // ── Anlagen-Checkliste ────────────────────────────────────────────────────
  lageVorhanden: "Beleg vorhanden",
  lagePruefen: "prüfen",
  lageFehlt: "kein Beleg",
  keineAnlagenTitel: "Dieser Fragebogen fordert keine Anlagen an",
  keineAnlagenHinweis:
    "Beleg-Fragen erkennt das System an Feldern vom Typ „Anlage“ und an Formulierungen wie „… included?“ oder „… liegt bei“.",
  belegFragen: (n: number) => `${n} Beleg-Fragen:`,
  zaehlerVorhanden: (n: number) => `${n} vorhanden`,
  zaehlerPruefen: (n: number) => `${n} prüfen`,
  zaehlerFehlt: (n: number) => `${n} fehlen`,
  registryGefiltert: (produkt: string, belege: number) =>
    `Registry gefiltert auf ${produkt} · ${belege} Belege`,
  registryGlobal: "global",
  abgleichHinweis:
    "Der Abgleich ist ein Vorschlag: er vergleicht die Frage mit Titel, Art und Themen des Belegs. Bitte den genannten Beleg selbst ansehen, bevor der Haken ins Formular geht.",
  aktuellTooltip: "Aktuellste Fassung laut Registry",
  aktuell: "aktuell",
  veraltetTooltip: "Es gibt eine neuere Fassung",
  veraltet: "veraltet",
  ausgestellt: (datum: string) => `ausgestellt ${datum}`,
  gueltigBis: (datum: string) => `· gültig bis ${datum}`,
  abgelaufen: " — abgelaufen",
  vertraulich: "Vertraulich.",
  uebereinstimmung: (score: string) => `· Übereinstimmung ${score}`,
  belegeBeilegen: (n: number) => `Beizulegende Belege (${n})`,
  weitereKandidaten: "Weitere Kandidaten:",
  weitereVertraulich: " — vertraulich",
  keinBelegHinweis:
    "In der Registry liegt kein passender Beleg für dieses Produkt. Entweder das Dokument fehlt wirklich — oder es ist noch nicht erfasst.",
  vertraulichHinweis:
    "Dieser Beleg ist als vertraulich gekennzeichnet. Das System sagt ihn deshalb NICHT automatisch zu — bitte vor dem Versand freigeben.",
  anlagenAbgelehntTitel: (n: number) => `Abgelehnt / nicht zutreffend (${n})`,
  anlagenAbgelehntHinweis:
    "Diese Beleg-Fragen sind mit „Nein“ oder „N/A“ beantwortet — die Belege werden dem Versand nicht beigelegt.",
  inSalesforceOeffnen: "In Salesforce öffnen",
  eigeneAnlagenTitel: "Anlagen ergänzen",
  eigeneAnlagenHinweis:
    "Fehlende Belege hier per Drag&Drop beilegen — sie kommen ins Download-Paket.",
  anlageAblegen: "Datei hierher ziehen oder klicken",
  anlageWirdHochgeladen: "wird hochgeladen …",
  anlageHochgeladen: (name: string) => `„${name}“ beigelegt`,
  anlageUploadFehlgeschlagen: "Anlage konnte nicht hochgeladen werden",
  anlageEntfernen: "Anlage entfernen",
  anlageLoeschenFehlgeschlagen: "Anlage konnte nicht entfernt werden",

  // ── K6: deterministische Gründe (grund_code) ──────────────────────────────
  // Der Server liefert zu jeder maschinell entschiedenen Frage einen CODE und
  // zusätzlich einen deutschen Satz. Gezeigt wird der Katalog-Text: der Satz
  // vom Server geht in Export und Log, wo niemand einen Katalog hat, und wäre
  // in der englischen Maske ein Fremdkörper (dieselbe Trennung wie beim
  // Norm-Guard).
  grundAnlageFehlt: "Kein Beleg dieser Art in der Anlagen-Registry.",
  grundAnlageUnsicher: (titel: string, score: string) =>
    `Beleg nur vermutet: „${titel}" trifft mit ${score} — bitte prüfen.`,
  grundAnlageAbgelaufen: (titel: string, datum: string) =>
    `„${titel}" ist am ${datum} abgelaufen — vor dem Versand erneuern.`,
  grundAnlageVertraulich: (titel: string) =>
    `„${titel}" liegt vor, ist aber als vertraulich gekennzeichnet — Freigabe nötig, bevor er zugesagt wird.`,
  // `position` ist in ALLEN Texten hier die ANZEIGE-Nummer der Karte, nicht
  // `q_question.position` (0-basiert). Umgerechnet wird an der Aufrufstelle mit
  // `questionnaires/labels.ts::anzeigeNummer` — ein roh durchgereichtes
  // Server-Feld zeigte auf die Nachbarkarte (Tester-Befund QSP-091).
  grundGatingElternantwort: (position: number, antwort: string) =>
    `Folgefrage zu Frage #${position} — dort steht „${antwort}". Damit entfällt diese Frage; vorgeschlagen ist „N/A".`,
  grundGatingSektion: (praefix: string, position: number, antwort: string) =>
    `Abschnitt ${praefix} gilt nur bei einer anderen Antwort auf Frage #${position} („${antwort}").`,
  grundSignoffSignatur:
    "Stempelfeld — bleibt vorerst leer. Das Dokument wird gestempelt, nicht unterschrieben; der Stempel folgt mit der Signatur-Lösung und wird nicht vorbelegt.",
  grundSignoffTitelFehlt: (name: string) =>
    `Für ${name} ist keine Funktion hinterlegt (Benutzerprofil) — bitte eintragen oder von Hand ausfüllen.`,
  grundSignoffKeinNutzer:
    "Zum Hochladenden gibt es keinen Benutzer-Datensatz — Name und Funktion können nicht aus dem System kommen.",
  grundAnlageZuordnungMittel: (titel: string, score: string) =>
    `Beleg-Zuordnung nur mittel (${score}) — „${titel}" vor der Zusage selbst ansehen.`,

  // ── K6: Herkunft einer maschinell entschiedenen Antwort ───────────────────
  herkunftRegistry: "aus der Anlagen-Registry",
  herkunftSystem: "aus dem Systemzustand",
  herkunftRegistryTooltip:
    "Diese Antwort folgt aus dem Bestand der Anlagen-Registry — nicht aus einer Bibliotheks-Antwort und nicht aus einem Sprachmodell.",
  herkunftSystemTooltip:
    "Diese Antwort folgt aus dem angemeldeten Benutzer und dem Ausfülldatum — nicht aus einer Bibliotheks-Antwort und nicht aus einem Sprachmodell.",

  // ── K7: Stammdaten direkt aus dem Faktenblatt ─────────────────────────────
  herkunftFaktenblatt: (key: string, stand: string) =>
    stand ? `Faktenblatt: ${key} (Stand ${stand})` : `Faktenblatt: ${key}`,
  herkunftFaktenblattTooltip:
    "Diese Antwort ist der Faktenblatt-Wert selbst — kein Retrieval-Treffer und kein formulierter Satz. Geprüft wird der Wert, nicht die Formulierung.",
  grundStammdatenOffenerTodo: (key: string) =>
    `Zu ${key} ist im Faktenblatt ein Punkt offen — deshalb nicht „sicher". Der offene Punkt steht unten.`,
  gatingHinweis: (position: number, text: string) =>
    `Folgefrage zu #${position}: „${text}"`,

  // ── F6/B1: Entscheidung inline in der Zeile ───────────────────────────────
  // Der Stapel oben ist weg — jede Entscheidung wird in ihrer Zeile kompakt
  // aufgeklappt. Der Kontext einer Kontakt-/Beleg-/Umsatz-Entscheidung sagt der
  // Katalog, nicht ein durchgereichter Serversatz.
  entscheidungOffen: "Entscheidung offen",
  entscheidungAufklappen: "Zum Entscheiden aufklappen",
  entscheidungEinklappen: "Einklappen",
  belegHerausgabeFrage: "An diesen Kunden herausgeben?",
  belegHerausgabeFehlt:
    "Für diese Anforderung liegt kein Standard-Beleg vor. Ob das Dokument für diesen Kunden erstellt und herausgegeben wird, ist eine Entscheidung.",
  belegHerausgabeVertraulich: (titel: string) =>
    `„${titel}" liegt vor, ist aber als vertraulich gekennzeichnet. Die Herausgabe an diesen Kunden ist eine Freigabe-Entscheidung.`,

  // ── F6/B2: Kontakt aus dem Mitarbeiter-Verzeichnis ────────────────────────
  verzeichnisWaehlen: "Aus Verzeichnis wählen …",
  verzeichnisOption: (name: string, zusatz: string) =>
    zusatz ? `${name} · ${zusatz}` : name,
  verzeichnisHinweis:
    "Person aus dem Mitarbeiter-Verzeichnis wählen — die Felder werden gefüllt und bleiben überschreibbar.",

  // ── F6/B3: Umsatz aus Salesforce (Entscheidung) ───────────────────────────
  umsatzTitel: "Umsatz aus Salesforce",
  umsatzEmpfehlung: (wert: string) => `Empfohlener Umsatz: ${wert}`,
  umsatzBasisScenario: (jahr: number, eur: string, usd: string) =>
    `Scenario Master ${jahr} (Current RFC — Finished Goods + Licenses + Medical Raw): ${eur} EUR ≈ ${usd} USD.`,
  umsatzBasisFakt: (wert: string) =>
    `Salesforce nicht erreichbar — Umsatzangabe aus dem Faktenblatt (${wert}).`,
  umsatzFreigabeHinweis:
    "Die Weitergabe der Umsatzzahl ist zusätzlich eine kaufmännische Freigabe — bitte bestätigen.",
  umsatzUebernehmen: "Empfehlung einsetzen",

  // ── F6/B8: Zweite Sammelaktion ────────────────────────────────────────────
  alleAbhaken: "Alles Übrige abhaken",
  alleAbhakenTooltip:
    "Setzt alle übrigen offenen Vorschläge (jeder Konfidenz, auch Entwürfe) auf „geprüft“ und die restlichen offenen Fragen auf „ausgelassen“. Entscheidungen und Lücken bleiben unberührt — es geht ohnehin noch durch die finale Prüfung.",
  abhakenFehlgeschlagen: "Abhaken fehlgeschlagen",
  allesAbgehakt: (uebernommen: number, uebersprungen: number) =>
    `${uebernommen} übernommen · ${uebersprungen} ausgelassen`,

  // ── F6/B9: wörtliche Duplikate im Formular ────────────────────────────────
  duplikatHinweis: (position: number) =>
    `Wortgleich mit Frage #${position} — im Formular doppelt vorhanden.`,
  duplikatUebernehmen: (position: number) => `Antwort von #${position} übernehmen`,
};

export type QuestionnairesKatalog = typeof questionnaires;
